import React, { lazy, Suspense, useEffect, useState } from 'react';
import styled from 'styled-components';
import * as yup from 'yup';
import _get from 'lodash/get';
import { Controller, useForm } from 'react-hook-form';
import { useSelector, useDispatch } from 'react-redux';
import { yupResolver } from '@hookform/resolvers/yup';
import CopyUrl from '../../../../../styledComponents/styles/CopyOrOpenUrlTextComponent.styled';
import CurrencyInput from '../../../../../styledComponents/styles/CurrencyInput.styled';
import StyledIcon from '../../../../../styledComponents/styles/Icon.styled';
import TextInput from '../../../../../styledComponents/styles/TextInput.styled';
import { AccentButtonMediumNunito } from '../../../../../styledComponents/styles/Buttons.styled';
import { AccentButtonSpinner } from '../../../../../styledComponents/styles/spinner.styled';
import { Paragraph } from '../../../../../styledComponents/styles/Paragraph.styled';
import { updateForm, updateQuestion } from '../../../../../actions/formsActions';
import Toggle from '../../../../../styledComponents/styles/Toggle.styled';

const EditTextArea = lazy(() => import('../../../../../styledComponents/styles/EditTextArea.styled'));

const FormContainer = styled.div`
  font-family: 'Nunito', sans-serif;
  font-style: normal;
  font-weight: 400;
  font-size: 14px;
  line-height: 120%;
  letter-spacing: 0.015em;
  color: #373737;
`;

const InputContainer = styled.div`
  flex: 1;
  margin: 20px 0;
  text-align: left;
`;

const CurrencyContainer = styled.div`
  flex: 1;
  margin: 20px 0 20px 20px;
  text-align: left;
`;

const CurrencyDiv = styled.div`
  line-height: 44px;
  font-size: 14px;
  font-weight: 400;
  font-family: Open Sans, sans-serif;

  span {
    display: inline-block;
    line-height: normal;
    vertical-align: middle;
  }
`;

const Label = styled(Paragraph)`
  color: #043544;
  font-size: 16px;
  font-weight: 600;
  font-family: Open Sans, sans-serif;
`;

const CostContainer = styled.div`
  display: flex;
  margin: 20px 0;
  text-align: left;
`;

const SubmitContainer = styled.main`
  display: flex;
  justify-content: flex-end;
  margin-top: 35px;
`;

const ToggleContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: inherit;
`;

const ToggleLabel = styled.span`
  align-self: center;
  flex: 1;
  justify-content: center;
  padding-left: 10px;
  padding-bottom: 7px;
`;

export default function QuestionCashnetCodeAddEdit({ question, edit, onClose }) {
  const MAX_LENGTH = 90;
  const qIdentifier = 'cash_net_with_code';
  const dispatch = useDispatch();
  const fetchForm = useSelector(state => state.forms.fetchForm);
  const questionTypesInfo = useSelector(state => state.forms.questionTypes);
  const updateFormSubmit = useSelector(state => state.forms.updateForm);
  const updateQuestionSubmit = useSelector(state => state.forms.updateQuestion);
  const [formId, setFormId] = useState(null);
  const organization = useSelector(state => state.auth.organization);
  const [linkUrl, setLinkUrl] = useState('');
  const [disableSubmit, setDisableSubmit] = useState(false);

  const schema = yup.object().shape({
    cash_net_with_code_label: yup
      .string()
      .required('Question must have label')
      .max(MAX_LENGTH, `Label can not be more than ${MAX_LENGTH} characters`),
    cash_net_with_code_instructions: yup.string(),
    cash_net_with_code_admin_only: yup.boolean(),
    cash_net_with_code_required: yup.boolean(),
    cash_net_with_code_cost: yup.string(),
    cash_net_with_code_currency: yup.string(),
    cash_net_with_code_url: yup.string().required('Transact Store URL must be a valid URL'),
  });

  const { control, handleSubmit, formState, setValue, getValues } = useForm({
    shouldUnregister: false,
    resolver: yupResolver(schema),
    defaultValues: {
      cash_net_with_code_label: edit ? question.label : '',
      cash_net_with_code_instructions: edit ? question.instructions || '' : '',
      cash_net_with_code_required: edit ? question.required : false,
      cash_net_with_code_cost: edit ? Number(question.cost)?.toFixed(2) : '0.00',
      cash_net_with_code_currency: 'USD',
      cash_net_with_code_url: edit ? question.url : '',
    },
    mode: 'onChange',
  });

  useEffect(() => {
    if (!fetchForm?.loading && fetchForm?.data) {
      setDisableSubmit(false);
      setFormId(fetchForm.data.id);
    }
  }, [fetchForm]);

  useEffect(() => {
    if (
      (!updateFormSubmit?.loading && updateFormSubmit?.data) ||
      (!updateQuestionSubmit?.loading && updateQuestionSubmit?.data)
    ) {
      setDisableSubmit(false);
      onClose();
    }
  }, [updateFormSubmit, updateQuestionSubmit]);

  useEffect(() => {
    if (organization) {
      setLinkUrl(`https://${organization.info.subdomain}.via-trm-api.com/api/cash_net/postback_with_code`);
    }
  }, [organization]);

  const onSubmitHandler = data => {
    if (questionTypesInfo && questionTypesInfo.data) {
      const questionType = questionTypesInfo.data.reduce((prev, curr) => {
        return prev || curr.attributes.question_types.find(question => question.identifier === qIdentifier);
      }, undefined);

      const values = {
        instructions: data.cash_net_with_code_instructions,
        label: data.cash_net_with_code_label,
        required: data.cash_net_with_code_required,
        currency: data.cash_net_with_code_currency,
        cost: data.cash_net_with_code_cost,
        url: data.cash_net_with_code_url,
      };

      if (formId && data && (questionType?.id || question?.id)) {
        //update or save new
        const actionToCall = edit ? updateQuestion : updateForm;
        const idToUpdate = edit ? question.id : questionType.id;
        setDisableSubmit(true);
        dispatch(actionToCall(formId, questionType.endpoint, idToUpdate, values));
      }
    }
  };

  return (
    <FormContainer>
      <form id={'cashnetCodeAdd'} onSubmit={handleSubmit(onSubmitHandler)}>
        <Controller
          control={control}
          name={'cash_net_with_code_label'}
          render={({ field: { ref, ...rest } }) => (
            <InputContainer>
              <TextInput
                aria-label={'Transact with Code Label'}
                errorMsg={_get(formState.errors.cash_net_with_code_label, 'message') || ''}
                id={'cash_net_with_code_label'}
                label={'Transact w/ Code Payment Label'}
                openSans
                placeholder={'Enter Label'}
                required
                {...rest}
              />
            </InputContainer>
          )}
        />
        <Controller
          control={control}
          name={'cash_net_with_code_instructions'}
          render={({ field: { ref, ...rest } }) => (
            <InputContainer>
              <Suspense fallback={<div />}>
                <EditTextArea
                  aria-label="Instructions"
                  errorMsg={_get(formState.errors.cash_net_with_code_instructions, 'message') || ''}
                  placeholder={`Enter Instructions`}
                  label={`Instructions`}
                  id={'cash_net_with_code_instructions'}
                  {...rest}
                />
              </Suspense>
            </InputContainer>
          )}
        />

        <CostContainer>
          <Controller
            control={control}
            name={'cash_net_with_code_cost'}
            render={({ field: { onChange, register, ref, ...rest } }) => (
              <InputContainer>
                <CurrencyInput
                  aria-label={'Transact Cost'}
                  errorMsg={_get(formState.errors.cash_net_with_code_cost, 'message') || ''}
                  id={'cash_net_with_code_cost'}
                  label={'Cost'}
                  openSans
                  required
                  onChange={value => {
                    setValue('cash_net_with_code_cost', value, {
                      shouldValidate: true,
                      shouldDirty: true,
                      shouldTouch: true,
                    });
                  }}
                  {...rest}
                />
              </InputContainer>
            )}
          />
          <CurrencyContainer>
            <Label>Currency</Label>
            <CurrencyDiv>
              <span>USD ($)</span>
            </CurrencyDiv>
          </CurrencyContainer>
        </CostContainer>
        <Controller
          control={control}
          name={'cash_net_with_code_url'}
          render={({ field: { ref, ...rest } }) => (
            <InputContainer>
              <TextInput
                aria-label={'Transact w/ Code Url Label'}
                errorMsg={_get(formState.errors.cash_net_with_code_url, 'message') || ''}
                id={'cash_net_with_code_url'}
                label={'Transact Store URL'}
                openSans
                placeholder={'Enter your Store URL'}
                required
                {...rest}
              />
            </InputContainer>
          )}
        />

        <InputContainer>
          <CopyUrl
            copyOnly
            buttonText={'Copy'}
            id={'cash_net_with_code_url'}
            linkUrl={linkUrl}
            label={'Via TRM Postback Gateway'}
            tooltip={`Send this link to your institution's IT department to POST transaction information`}
          />
        </InputContainer>
        <Controller
          control={control}
          name={'cash_net_with_code_required'}
          render={({ field: { onChange, register, ref, ...rest } }) => (
            <ToggleContainer>
              <Toggle
                id={'cash_net_with_code_required'}
                label={'Required'}
                onChange={value => {
                  setValue('cash_net_with_code_required', value);
                }}
                ref={register}
                {...rest}
              />
              <ToggleLabel>Required Field</ToggleLabel>
            </ToggleContainer>
          )}
        />
        <SubmitContainer>
          <AccentButtonMediumNunito
            aria-label={edit ? 'Save question' : 'Add Transact with code to Form'}
            form="cashnetCodeAdd"
            type={'submit'}
            disabled={disableSubmit}
          >
            {disableSubmit ? <AccentButtonSpinner displayText="Saving ..." /> : edit ? 'Save' : 'Add to Form'}
            {!disableSubmit && <StyledIcon type="ChevronForward" color="#fff" />}
          </AccentButtonMediumNunito>
        </SubmitContainer>
      </form>
    </FormContainer>
  );
}
